import { Routes } from '@angular/router';
import { guestGuard, guestMatchGuard, authMatchGuard, hasTokenGuard } from './core/guards/auth.guard';
import { roleMatchGuard } from './core/guards/role.guard';
import { ROUTE_SEGMENTS } from './core/constants/routes.constants';
import { ProjectService } from './core/services/project.service';
import { SettingsService } from './core/services/settings.service';

export const routes: Routes = [
  // Landing
  {
    path: ROUTE_SEGMENTS.home,
    pathMatch: 'full',
    canActivate: [guestGuard],
    loadComponent: () => import('./landing/landing-page/landing-page.component').then(m => m.LandingComponent),
  },

  // Auth — redirect to dashboard if already logged in
  {
    path: ROUTE_SEGMENTS.auth.root,
    canMatch: [guestMatchGuard],
    loadComponent: () => import('./layouts/auth-layout/auth-layout.component').then(m => m.AuthLayout),
    loadChildren: () => import('./auth/auth.routes').then(m => m.authRoutes),
  },

  // Super Admin — must be authenticated + have super_admin role
  {
    path: ROUTE_SEGMENTS.superAdmin.root,
    canMatch: [authMatchGuard, roleMatchGuard('super_admin')],
    providers: [
      ProjectService,
      SettingsService,
    ],
    loadComponent: () => import('./layouts/admin-layout/admin-layout.component').then(m => m.AdminLayout),
    loadChildren: () => import('./admin/super-admin.routes').then(m => m.superAdminRoutes),
  },

  // Admin invite token — render signup at /admin?token=... without redirect
  {
    path: ROUTE_SEGMENTS.admin.root,
    canMatch: [hasTokenGuard],
    loadComponent: () => import('./layouts/auth-layout/auth-layout.component').then(m => m.AuthLayout),
    loadChildren: () => [{
      path: '',
      pathMatch: 'full',
      loadComponent: () => import('./auth/admin-signup/admin-signup.component').then(m => m.AdminSignupComponent),
    }],
  },

  // Admin — must be authenticated + have admin role
  {
    path: ROUTE_SEGMENTS.admin.root,
    canMatch: [authMatchGuard, roleMatchGuard('admin')],
    providers: [
      ProjectService,
      SettingsService,
    ],
    loadComponent: () => import('./layouts/admin-layout/admin-layout.component').then(m => m.AdminLayout),
    loadChildren: () => import('./admin/admin.routes').then(m => m.adminRoutes),
  },

  // Builder — must be authenticated + have builder role
  {
    path: ROUTE_SEGMENTS.builder.root,
    canMatch: [authMatchGuard, roleMatchGuard('builder')],
    providers: [
      ProjectService,
      SettingsService,
    ],
    loadComponent: () => import('./layouts/builder-layout/builder-layout.component').then(m => m.BuilderLayout),
    loadChildren: () => import('./builder/builder.routes').then(m => m.builderRoutes),
  },

  // Guest (trial) — must be authenticated + have the trial role. Walks the same project
  // workflow as a builder project, just under its own portal/URL and a restricted menu.
  {
    path: ROUTE_SEGMENTS.guest.root,
    canMatch: [authMatchGuard, roleMatchGuard('trial')],
    providers: [
      ProjectService,
      SettingsService,
    ],
    loadComponent: () => import('./layouts/guest-layout/guest-layout.component').then(m => m.GuestLayout),
    loadChildren: () => import('./guest/guest.routes').then(m => m.guestRoutes),
  },

  // Customer Service — must be authenticated + have customer_service role
  {
    path: ROUTE_SEGMENTS.customerService.root,
    canMatch: [authMatchGuard, roleMatchGuard('customer_service')],
    providers: [
      ProjectService,
      SettingsService,
    ],
    loadComponent: () => import('./layouts/customer-service-layout/customer-service-layout.component').then(m => m.CustomerServiceLayout),
    loadChildren: () => import('./customer-service/customer-service.routes').then(m => m.customerServiceRoutes),
  },

  // Pricing report — standalone print page, no layout wrapper
  {
    path: ROUTE_SEGMENTS.pricingReport,
    title: 'Pricing Report',
    loadComponent: () => import('./features/pricing-report/pricing-report.component').then(m => m.PricingReportComponent),
  },

  // Token-based reset password (no auth layout) — accessed from email link
  {
    path: 'reset-password',
    title: 'Reset Password',
    loadComponent: () => import('./auth/token-reset-password/token-reset-password.component').then(m => m.TokenResetPasswordComponent),
  },

  // 404 — catch all unmatched routes
  {
    path: ROUTE_SEGMENTS.notFound,
    loadComponent: () => import('./not-found/not-found.component').then(m => m.NotFoundComponent),
  },
];
